import Select, { type FilterOptionOption } from "react-select";
import AsyncSelect from "react-select/async";
import CreatableSelect from "react-select/creatable";
import { adminSelectBaseProps } from "./adminSelectStyles";

type FinalFormInput = {
	value: unknown;
	onChange: (v: unknown) => void;
	onBlur?: () => void;
	onFocus?: () => void;
};

type CreatableOption = {
	label: string;
	value: string | number;
	__isNew__?: boolean;
};

function isValidNewCreatableOption(
	inputValue: string,
	_selected: readonly CreatableOption[],
	options: readonly CreatableOption[],
): boolean {
	const trimmedInputValue = inputValue.trim();
	if (trimmedInputValue.length === 0) {
		return false;
	}

	const normalizedInputValue = trimmedInputValue.toLowerCase();
	return options.every((option) => {
		if (option.__isNew__ === true) {
			return true;
		}

		const optionValue = String(option.value).toLowerCase();
		const optionLabel = option.label.toLowerCase();
		return optionValue !== normalizedInputValue && optionLabel !== normalizedInputValue;
	});
}

function formatCreatableLabel(inputValue: string): string {
	return `Create "${inputValue.trim()}"`;
}

function getNewCreatableOptionData(inputValue: string, optionLabel: string): CreatableOption {
	const trimmedValue = inputValue.trim();

	return {
		label: typeof optionLabel === "string" ? optionLabel : formatCreatableLabel(trimmedValue),
		value: trimmedValue,
		__isNew__: true,
	};
}

function filterCreatableOption(option: FilterOptionOption<CreatableOption>, rawInput: string): boolean {
	if (option.data.__isNew__ === true) {
		return true;
	}

	const candidate = `${option.label} ${option.value}`.toLowerCase();
	return candidate.includes(rawInput.toLowerCase());
}

export function AdminCreatableSelectAdapter({
	input,
	meta: _meta,
	formatCreateLabel = formatCreatableLabel,
	isValidNewOption = isValidNewCreatableOption,
	getNewOptionData = getNewCreatableOptionData,
	filterOption = filterCreatableOption,
	...rest
}: {
	input: FinalFormInput;
	meta?: unknown;
	formatCreateLabel?: (inputValue: string) => string;
	isValidNewOption?: (
		inputValue: string,
		selected: readonly CreatableOption[],
		options: readonly CreatableOption[],
	) => boolean;
	getNewOptionData?: (inputValue: string, optionLabel: string) => CreatableOption;
	filterOption?: (option: FilterOptionOption<CreatableOption>, rawInput: string) => boolean;
	[x: string]: unknown;
}) {
	return (
		<CreatableSelect<CreatableOption, false>
			{...adminSelectBaseProps}
			{...rest}
			formatCreateLabel={formatCreateLabel}
			isValidNewOption={isValidNewOption}
			getNewOptionData={getNewOptionData}
			filterOption={filterOption}
			value={(input.value as CreatableOption | null) || null}
			onChange={(value) => input.onChange(value)}
			onBlur={input.onBlur}
			onFocus={input.onFocus}
		/>
	);
}

export function AdminSelectAdapter({
	input,
	meta: _meta,
	...rest
}: {
	input: FinalFormInput;
	meta?: unknown;
	[x: string]: unknown;
}) {
	return (
		<Select
			{...adminSelectBaseProps}
			{...rest}
			value={input.value}
			onChange={input.onChange}
			onBlur={input.onBlur}
			onFocus={input.onFocus}
		/>
	);
}

export function AdminAsyncSelectAdapter({
	input,
	meta: _meta,
	...rest
}: {
	input: FinalFormInput;
	meta?: unknown;
	[x: string]: unknown;
}) {
	return (
		<AsyncSelect
			{...adminSelectBaseProps}
			{...rest}
			value={input.value}
			onChange={input.onChange}
			onBlur={input.onBlur}
			onFocus={input.onFocus}
		/>
	);
}
